
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBookClass = () => {
    // Trigger Readdy Agent widget for booking
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) {
      widget.click();
    } else {
      // Fallback to contact page
      navigate('/contact');
    }
  };

  const handleLearnMore = () => {
    navigate('/about');
  };

  const features = [
    {
      icon: 'ri-user-star-line',
      title: t('about.reasons.experience'),
      description: t('about.reasons.experienceDesc')
    },
    {
      icon: 'ri-focus-3-line',
      title: t('about.reasons.personalized'),
      description: t('about.reasons.personalizedDesc')
    },
    {
      icon: 'ri-time-line',
      title: t('about.reasons.flexible'),
      description: t('about.reasons.flexibleDesc')
    },
    {
      icon: 'ri-trophy-line',
      title: t('about.reasons.results'),
      description: t('about.reasons.resultsDesc')
    }
  ];

  const stats = [
    { number: '1000+', label: t('stats.students') },
    { number: '20+', label: t('stats.experience') },
    { number: '95%', label: t('stats.success') },
    { number: '24/7', label: t('stats.support') }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section with Motion Video Banner */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover object-top"
            poster="https://readdy.ai/api/search-image?query=Bright%20vibrant%20online%20classroom%20scene%20with%20diverse%20students%20actively%20learning%20mathematics%20and%20english%20on%20modern%20devices%2C%20professional%20teacher%20conducting%20engaging%20live%20lesson%20on%20large%20interactive%20screen%2C%20colorful%20educational%20environment%20with%20natural%20lighting%20streaming%20through%20windows%2C%20high%20quality%20professional%20educational%20technology%20setup%20with%20green%20plants%20and%20modern%20furniture%2C%20engaging%20digital%20learning%20atmosphere%20with%20emerald%20green%20and%20teal%20accents%2C%20modern%20bright%20classroom%20interior%20with%20students%20smiling%20and%20participating%2C%20educational%20excellence%20and%20success%2C%20professional%20online%20education%20setting&width=1920&height=1080&seq=home-hero-video-poster-unique&orientation=landscape"
          >
            <source src="https://videos.pexels.com/video-files/5198252/5198252-uhd_2560_1440_25fps.mp4" type="video/mp4" />
            <source src="https://videos.pexels.com/video-files/5198252/5198252-hd_1920_1080_25fps.mp4" type="video/mp4" />
            <source src="https://videos.pexels.com/video-files/5198252/5198252-sd_640_360_25fps.mp4" type="video/mp4" />
          </video>
          {/* Overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/30 via-emerald-500/20 to-teal-600/30"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <div className="inline-flex items-center px-6 py-3 bg-white/95 backdrop-blur-sm rounded-full text-emerald-700 font-bold text-lg mb-8 shadow-2xl border-2 border-emerald-200">
                <i className="ri-medal-line text-yellow-500 mr-3 text-xl"></i>
                {t('hero.experience')}
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight drop-shadow-2xl">
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">
                {t('hero.title')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl mb-12 font-medium leading-relaxed drop-shadow-xl max-w-4xl mx-auto">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-2xl transform hover:scale-105 transition-all duration-300 px-12 py-6 text-xl font-bold border-2 border-white/30 rounded-xl"
                onClick={handleBookClass}
              >
                <i className="ri-calendar-check-line mr-3 text-xl"></i>
                {t('cta.bookClass')}
              </Button>
              <Button 
                size="lg" 
                className="bg-white/95 text-emerald-600 hover:bg-white hover:text-emerald-700 shadow-2xl transform hover:scale-105 transition-all duration-300 px-12 py-6 text-xl font-bold border-2 border-emerald-200 rounded-xl"
                onClick={handleLearnMore}
              >
                <i className="ri-play-circle-line mr-3 text-xl"></i>
                {t('cta.learnMore')}
              </Button>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-8 text-lg">
              <div className="flex items-center space-x-3 bg-white/20 backdrop-blur-sm px-6 py-4 rounded-full border border-white/30 shadow-xl">
                <i className="ri-user-star-fill text-yellow-300 text-2xl drop-shadow-lg"></i>
                <span className="font-semibold drop-shadow-md">{t('hero.qualifiedTeachers')}</span>
              </div>
              <div className="flex items-center space-x-3 bg-white/20 backdrop-blur-sm px-6 py-4 rounded-full border border-white/30 shadow-xl">
                <i className="ri-global-line text-blue-300 text-2xl drop-shadow-lg"></i>
                <span className="font-semibold drop-shadow-md">{t('hero.onlineLearning')}</span>
              </div>
              <div className="flex items-center space-x-3 bg-white/20 backdrop-blur-sm px-6 py-4 rounded-full border border-white/30 shadow-xl">
                <i className="ri-trophy-fill text-yellow-300 text-2xl drop-shadow-lg"></i>
                <span className="font-semibold drop-shadow-md">{t('hero.provenResults')}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Floating Educational Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center animate-bounce shadow-2xl">
          <i className="ri-calculator-line text-3xl text-white drop-shadow-lg"></i>
        </div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center animate-pulse shadow-2xl">
          <i className="ri-translate-2 text-2xl text-white drop-shadow-lg"></i>
        </div>
        <div className="absolute bottom-40 left-20 w-24 h-24 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center animate-bounce delay-1000 shadow-2xl">
          <i className="ri-book-open-line text-3xl text-white drop-shadow-lg"></i>
        </div>
        <div className="absolute top-1/3 right-10 w-18 h-18 bg-gradient-to-br from-green-4   00 to-green-600 rounded-full flex items-center justify-center animate-pulse delay-500 shadow-2xl">
          <i className="ri-graduation-cap-line text-2xl text-white drop-shadow-lg"></i>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-6">
              {t('about.whyChoose')}
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed font-medium">
              {t('about.mission')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-emerald-100 hover:border-emerald-200">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg">
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-800/20 to-teal-800/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('stats.title')}
            </h2>
            <p className="text-xl text-white/95 font-medium">
              {t('stats.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-500 bg-clip-text text-transparent drop-shadow-lg">
                  {stat.number}
                </div>
                <div className="text-lg md:text-xl font-semibold text-white/95">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Preview */}
      <section className="py-20 bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-6">
              {t('courses.title')}
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 font-medium">
              {t('courses.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Mathematics Course */}
            <div className="bg-gradient-to-br from-emerald-50 via-emerald-100/70 to-teal-50 rounded-3xl p-10 border-2 border-emerald-200 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1">
              <div className="flex items-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                  <i className="ri-calculator-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('courses.mathematics')}</h3>
                  <p className="text-gray-700 text-lg font-medium">{t('courses.mathDescription')}</p>
                </div>
              </div>
              
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center py-4 px-6 bg-white rounded-xl shadow-md border border-emerald-100 hover:border-emerald-200 transition-colors">
                  <span className="font-bold text-gray-800">{t('courses.oneOnOne')}</span>
                  <span className="text-emerald-600 font-bold text-xl">250 UAH</span>
                </div>
                <div className="flex justify-between items-center py-4 px-6 bg-white rounded-xl shadow-md border border-emerald-100 hover:border-emerald-200 transition-colors">
                  <span className="font-bold text-gray-800">{t('courses.twoStudents')}</span>
                  <span className="text-emerald-600 font-bold text-xl">175 UAH</span>
                </div>
                <div className="flex justify-between items-center py-4 px-6 bg-white rounded-xl shadow-md border border-emerald-100 hover:border-emerald-200 transition-colors">
                  <span className="font-bold text-gray-800">{t('courses.fourStudents')}</span>
                  <span className="text-emerald-600 font-bold text-xl">100 UAH</span>
                </div>
              </div>
              
              <Button 
                className="w-full text-lg py-4 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                onClick={handleBookClass}
              >
                <i className="ri-user-add-line mr-3"></i>
                {t('cta.enroll')}
              </Button>
            </div>

            {/* English Course */}
            <div className="bg-gradient-to-br from-teal-50 via-teal-100/70 to-emerald-50 rounded-3xl p-10 border-2 border-teal-200 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1">
              <div className="flex items-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                  <i className="ri-translate-2 text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('courses.english')}</h3>
                  <p className="text-gray-700 text-lg font-medium">{t('courses.englishDescription')}</p>
                </div>
              </div>
              
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center py-4 px-6 bg-white rounded-xl shadow-md border border-teal-100 hover:border-teal-200 transition-colors">
                  <span className="font-bold text-gray-800">{t('courses.twoStudents')}</span>
                  <span className="text-teal-600 font-bold text-xl">275 UAH</span>
                </div>
                <div className="flex justify-between items-center py-4 px-6 bg-white rounded-xl shadow-md border border-teal-100 hover:border-teal-200 transition-colors">
                  <span className="font-bold text-gray-800">{t('courses.fourStudents')}</span>
                  <span className="text-teal-600 font-bold text-xl">225 UAH</span>
                </div>
                <div className="flex justify-between items-center py-4 px-6 bg-white rounded-xl shadow-md border border-teal-100 hover:border-teal-200 transition-colors">
                  <span className="font-bold text-gray-800">{t('courses.sixStudents')}</span>
                  <span className="text-teal-600 font-bold text-xl">175 UAH</span>
                </div>
              </div>
              
              <Button 
                variant="secondary" 
                className="w-full text-lg py-4 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white"
                onClick={handleBookClass}
              >
                <i className="ri-user-add-line mr-3"></i>
                {t('cta.enroll')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-800/20 to-teal-800/20"></div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-5xl font-bold mb-8">
            {t('cta.readyToStart')}
          </h2>
          <p className="text-xl md:text-2xl mb-12 font-medium leading-relaxed">
            {t('cta.joinThousands')}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-emerald-600 hover:bg-gray-50 hover:text-emerald-700 shadow-2xl transform hover:scale-105 transition-all duration-300 px-10 py-5 text-xl font-bold"
              onClick={handleBookClass}
            >
              <i className="ri-calendar-check-line mr-3 text-xl"></i>
              {t('cta.bookClass')}
            </Button>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-yellow-400 to-yellow-5
0 text-gray-900 hover:from-yellow-500 hover:to-yellow-600 shadow-2xl transform hover:scale-105 transition-all duration-300 px-10 py-5 text-xl font-bold"
              onClick={() => navigate('/contact')}
            >
              <i className="ri-customer-service-2-line mr-3 text-xl"></i>
              {t('cta.contact')}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
